import React, { useCallback, useEffect, useState } from "react";
import { printApi, apiError } from "../../api/lab";
import { useAuth } from "../../AuthProvider";
import { useApp } from "../../AppProvider";
import { Button, Badge, Modal, Field, Loading } from "../../Components/UI/ui";
import { GATE, openPdf, maskPhone, fmtDateTime, deliveryLine } from "../../lib/lab";

// Report printing and WhatsApp delivery for one booking (frontend guide §4).
// Drives its state from GET /labReports/appointment/:id/status; `refreshKey`
// re-checks after a publish (which also sends the report automatically).
const PrintReportButton = ({ appointmentId, refreshKey, compact = false }) => {
  const { can } = useAuth();
  const { toast, defaultLayout, loadBranding } = useApp();
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(null);
  const [menu, setMenu] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const [sendErr, setSendErr] = useState(null);
  const [history, setHistory] = useState(null);   // null = closed, [] = loading done

  const load = useCallback(async () => {
    if (!appointmentId) return;
    try { setSt(await printApi.reportStatus(appointmentId)); } catch { setSt(null); }
  }, [appointmentId]);

  useEffect(() => { load(); loadBranding(); }, [load, refreshKey, loadBranding]);

  const open = (opts) => {
    setMenu(false);
    openPdf(printApi.reportPdfUrl(appointmentId, { layout: opts.layout || defaultLayout, download: opts.download }));
  };

  const attach = async () => {
    setBusy("attach");
    try {
      await printApi.attachReport(appointmentId);
      toast.success(st?.attachedFileId ? "Attached PDF regenerated" : "Report attached to the patient's record");
      load();
    } catch (e) { toast.error(e); } finally { setBusy(null); }
  };

  // POST …/send — to the patient, or to another number (attendant, relative)
  const send = async (toPhone) => {
    setBusy("send"); setSendErr(null);
    try {
      const res = await printApi.sendReport(appointmentId, toPhone);
      // Prefer our own line so the number is masked the same way everywhere
      toast.success(res?.delivery ? deliveryLine(res.delivery) : res?.message || "Report sent");
      setSendOpen(false); setPhone("");
      load();
    } catch (e) {
      const msg = apiError(e);
      if (sendOpen) setSendErr(msg); else toast.error(msg);
      load(); // a failed attempt is still logged
    } finally { setBusy(null); }
  };

  const openHistory = async () => {
    setHistory("loading");
    try { setHistory((await printApi.deliveries(appointmentId))?.deliveries || []); }
    catch (e) { toast.error(e); setHistory(null); }
  };

  if (!st) return null;
  const n = st.published?.length ?? 0, m = n + (st.pending?.length ?? 0);
  const partial = st.canPrint && !st.allPublished;
  const label = !st.canPrint ? "Print report" : partial ? `Print partial report (${n} of ${m})` : "Print report";
  const last = st.lastDelivery;
  const canSend = st.canPrint && can(...GATE.sendReport);

  return (
    <div className="lab-row" style={{ position: "relative", gap: 6, flexWrap: "wrap" }}>
      <Button
        size={compact ? "sm" : undefined}
        variant={!st.canPrint ? "secondary" : partial ? "primary" : "navy"}
        disabled={!st.canPrint}
        title={!st.canPrint ? "No test released yet" : partial ? `Pending: ${st.pending.map((p) => `${p.name} (${p.label})`).join(", ")}` : ""}
        onClick={() => setMenu((x) => !x)}
      >
        🖨 {label} ▾
      </Button>
      {menu && (
        <div className="lab-list" style={{ position: "absolute", top: "100%", left: 0, marginTop: 4, zIndex: 20, background: "#fff", padding: 6, borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,.18)", minWidth: 220 }}>
          <div className="lab-list__item" onClick={() => open({ layout: "branded" })}>Branded {defaultLayout === "branded" && <span className="lab-muted">default</span>}</div>
          <div className="lab-list__item" onClick={() => open({ layout: "letterhead" })}>Letterhead {defaultLayout === "letterhead" && <span className="lab-muted">default</span>}</div>
          <div className="lab-list__item" onClick={() => open({ download: true })}>Download PDF</div>
        </div>
      )}

      {canSend && (
        <Button size="sm" variant={last?.status === "failed" ? "danger" : "secondary"} loading={busy === "send"} onClick={() => send()}
          title={partial ? "Send the released tests to the patient on WhatsApp" : "Send the report to the patient on WhatsApp"}>
          {last?.status === "failed" ? "Retry WhatsApp" : last ? "Resend on WhatsApp" : "Send on WhatsApp"}
        </Button>
      )}
      {canSend && <Button size="sm" variant="ghost" onClick={() => { setSendOpen(true); setPhone(""); setSendErr(null); }} title="Send to a different number">To another number…</Button>}

      {last && (
        <button type="button" className="lab-link" onClick={openHistory} title="Delivery history">
          {last.status === "sent"
            ? <Badge kind="on">Sent ✓ {fmtDateTime(last.sentAt).split(", ").pop()}{st.deliveryCount > 1 ? ` · ${st.deliveryCount}` : ""}</Badge>
            : <Badge kind="unpaid">Not delivered</Badge>}
        </button>
      )}

      {st.allPublished && st.attachedFileId && <Badge kind="on">Attached to patient record</Badge>}
      {st.allPublished && can(...GATE.attachReport) && (
        <Button size="sm" variant="ghost" loading={busy === "attach"} onClick={attach} title="Regenerate the PDF stored on the patient's record (e.g. after a letterhead change)">
          {st.attachedFileId ? "Regenerate attached PDF" : "Attach report"}
        </Button>
      )}
      {partial && !compact && (
        <span className="lab-muted" style={{ fontSize: 12 }}>
          Pending: {st.pending.map((p) => `${p.name} — ${p.label}`).join(" · ")}
        </span>
      )}

      <Modal open={sendOpen} onClose={() => setSendOpen(false)} size="sm" title="Send report to another number"
        footer={<>
          {sendErr && <span className="lab-danger-text" style={{ marginRight: "auto" }}>{sendErr}</span>}
          <Button onClick={() => setSendOpen(false)}>Cancel</Button>
          <Button variant="primary" loading={busy === "send"} disabled={phone.replace(/\D/g, "").length < 10} onClick={() => send(phone.replace(/\D/g, "").slice(-10))}>Send</Button>
        </>}>
        <div className="lab-muted">For an attendant or relative. The patient's own number is used by default.</div>
        <Field label="WhatsApp number" hint="10 digits">
          <input className="lab-input w-full" value={phone} autoFocus inputMode="numeric" onChange={(e) => setPhone(e.target.value)} onKeyDown={(e) => e.key === "Enter" && phone.replace(/\D/g, "").length >= 10 && send(phone.replace(/\D/g, "").slice(-10))} />
        </Field>
      </Modal>

      <Modal open={history !== null} onClose={() => setHistory(null)} title="Report delivery history" footer={<Button onClick={() => setHistory(null)}>Close</Button>}>
        {history === "loading" ? <Loading /> : history?.length ? (
          <table className="lab-lines">
            <thead><tr><th>When</th><th>To</th><th>Report</th><th>Sent by</th><th>Status</th></tr></thead>
            <tbody>
              {[...history].reverse().map((d, i) => (
                <tr key={i}>
                  <td>{fmtDateTime(d.sentAt)}</td>
                  <td>{maskPhone(d.to)}</td>
                  <td>{d.partial ? <span className="lab-muted">Partial · {d.publishedCount} of {d.totalCount}</span> : "Final"}</td>
                  <td className="lab-muted">{d.sentBy?.name || "—"}{d.auto ? " · automatic" : ""}</td>
                  <td>{d.status === "sent" ? <Badge kind="on">Sent</Badge> : <><Badge kind="unpaid">Failed</Badge>{d.error && <div className="lab-danger-text" style={{ fontSize: 11 }}>{d.error}</div>}</>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <span className="lab-muted">No sends yet.</span>}
      </Modal>
    </div>
  );
};

export default PrintReportButton;
